import { CircleHelpIcon } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { ColorDot } from "@/components/app/record";
import { RosterProfileRow } from "@/components/app/roster-profile-row";
import { Card } from "@/components/ui/card";
import { useTheme } from "@/lib/ground";
import { kitEdge } from "@/lib/team-color";
import { useMediaQuery } from "@/lib/useMediaQuery";
import { cn } from "@/lib/utils";
import {
  breaksOf,
  gameSpanMs,
  hookupsOf,
  pointDurations,
  runsAndLeads,
  scoreAtHalf,
  scorersOf,
  worm,
} from "../../../shared/game-story";
import {
  type GameStats,
  isBreak,
  longestPointMs,
  type Player,
  playerLabel,
} from "../../../shared/types";
import { winProbabilitySeries } from "../../../shared/winprob";
import { useT } from "../../i18n";

/** What the charts need to know about a side: its code and its colour. */
export interface StorySide {
  short: string;
  color: string;
}

/**
 * The story of a finished game (board redesign, 2026-09-10), from its point
 * list alone: the worm and the win probability on one axis, the run of play
 * as blocks, a row of numbers, the point lengths, the scorers and the
 * hookups per side. Everything is SVG drawn by hand at a fixed viewBox, so
 * it scales with the card and ships no chart library.
 *
 * `target` is the winner's final score: the game's target unless a cap cut
 * it short, and either way the honest race the record shows.
 */
export function GameStory({
  snap,
  a,
  b,
  profiles,
}: {
  snap: GameStats;
  a: StorySide;
  b: StorySide;
  /** Roster row id to a public profile's account id: those names are links. */
  profiles?: ReadonlyMap<string, string> | undefined;
}) {
  const t = useT();
  const theme = useTheme();
  const wide = useMediaQuery(SM_QUERY);
  const w = wide ? W_WIDE : W_NARROW;

  // Which explanation is open, by the tile's or chart's key. One at a time:
  // a mouse moving down the grid trails one popover, not four.
  const [hint, setHint] = useState<string | null>(null);
  useEffect(() => {
    if (hint === null) return;
    const away = (e: PointerEvent): void => {
      if (e.target instanceof Element && e.target.closest("[data-hint-root]") !== null) return;
      setHint(null);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [hint]);
  /** The handlers a tile or a chart header wears: hover for a mouse, a tap
   *  for a finger. The mark inside handles the keyboard. */
  const hintProps = (id: string) => ({
    "data-hint-root": true,
    onPointerEnter: (e: React.PointerEvent): void => {
      if (e.pointerType === "mouse") setHint(id);
    },
    onPointerLeave: (e: React.PointerEvent): void => {
      if (e.pointerType === "mouse") setHint((cur) => (cur === id ? null : cur));
    },
    onPointerDown: (e: React.PointerEvent): void => {
      if (e.pointerType !== "mouse") setHint((cur) => (cur === id ? null : id));
    },
  });
  const toggleHint = (id: string): void => setHint((cur) => (cur === id ? null : id));
  const points = snap.points;
  if (points.length < 2) return null;
  const target = Math.max(snap.scoreA, snap.scoreB, 1);
  const series = winProbabilitySeries(points, target);
  const { longestRun, largestLead, leadChanges } = runsAndLeads(points);
  const breaks = breaksOf(points);
  const longest = longestPointMs(points);
  const span = gameSpanMs(points);
  const half = scoreAtHalf(points, target);
  const durations = pointDurations(points);
  const sideOf = (s: "A" | "B"): StorySide => (s === "A" ? a : b);
  const rosterOf = (s: "A" | "B"): Player[] =>
    (s === "A" ? snap.teamA.roster : snap.teamB.roster) ?? [];
  const nameOf = (s: "A" | "B", id: string): string => {
    const p = rosterOf(s).find((x) => x.id === id);
    return p === undefined ? "?" : playerLabel(p);
  };
  /** A hookup's name, a link when the player has a public profile. */
  const linkedName = (s: "A" | "B", id: string): ReactNode => {
    const profileId = profiles?.get(id);
    return profileId === undefined ? (
      nameOf(s, id)
    ) : (
      <a
        href={`/players/${encodeURIComponent(profileId)}`}
        className="underline underline-offset-4 hover:text-foreground"
      >
        {nameOf(s, id)}
      </a>
    );
  };

  const holds = points.filter((p) => !isBreak(p)).length;
  // Every tile carries its explanation: the labels are the sport's words
  // ("holds", "breaks") and a spectator's parent is reading too.
  const tiles: { id: string; label: string; value: string; hint: string; tone?: "a" | "b" }[] = [
    {
      id: "breaks",
      label: t("board.breaks"),
      value: `${a.short} ${breaks.A} · ${b.short} ${breaks.B}`,
      hint: t("board.story.hint.breaks"),
    },
    ...(longestRun !== null
      ? [
          {
            id: "longestRun",
            label: t("board.story.longestRun"),
            value: `${sideOf(longestRun.team).short} ×${longestRun.length}`,
            hint: t("board.story.hint.longestRun"),
            tone: longestRun.team === "A" ? ("a" as const) : ("b" as const),
          },
        ]
      : []),
    ...(largestLead !== null
      ? [
          {
            id: "largestLead",
            label: t("board.story.largestLead"),
            value: `+${largestLead.lead} ${sideOf(largestLead.team).short}`,
            hint: t("board.story.hint.largestLead"),
            tone: largestLead.team === "A" ? ("a" as const) : ("b" as const),
          },
        ]
      : []),
    {
      id: "leadChanges",
      label: t("board.story.leadChanges"),
      value: String(leadChanges),
      hint: t("board.story.hint.leadChanges"),
    },
    ...(half !== null
      ? [
          {
            id: "half",
            label: t("board.story.half"),
            value: `${half.scoreA}–${half.scoreB}`,
            hint: t("board.story.hint.half"),
          },
        ]
      : []),
    ...(longest !== null
      ? [
          {
            id: "longestPoint",
            label: t("board.longestPoint"),
            value: mmss(longest),
            hint: t("board.story.hint.longestPoint"),
          },
        ]
      : []),
    ...(span !== null
      ? [
          {
            id: "duration",
            label: t("board.story.duration"),
            value: minutes(span),
            hint: t("board.story.hint.duration"),
          },
        ]
      : []),
    // The real rate, not the win-probability model's smoothed one (a 0.7
    // prior worth 12 points, floored at 0.4, which once showed 4 of 18 as
    // "41%"); the count itself is in the explanation.
    {
      id: "holds",
      label: t("board.story.holds"),
      value: `${Math.round((holds / Math.max(points.length, 1)) * 100)}%`,
      hint: t("board.story.hint.holds", { holds, n: points.length }),
    },
  ];

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {tiles.map((tile, i) => {
          // The popover hangs off the tile's inner edge, so a tile in the
          // right half of the grid opens leftwards and stays on the page.
          const cols = wide ? 4 : 2;
          return (
            <Card
              key={tile.id}
              // `overflow-visible`: the Card clips by default, and the explanation
              // hangs BELOW the tile, over whatever is next.
              className="relative cursor-help gap-1.5 overflow-visible p-4"
              {...hintProps(tile.id)}
            >
              <span className="flex items-start justify-between gap-2">
                <span className="eyebrow text-muted-foreground">{tile.label}</span>
                <Hint
                  open={hint === tile.id}
                  onToggle={() => toggleHint(tile.id)}
                  text={tile.hint}
                  align={i % cols < cols / 2 ? "left" : "right"}
                />
              </span>
              {/* The value in the side's kit colour - unless the pane would
                  swallow it (`kitEdge`: a white kit on the light card), when it
                  is plain ink. A word cannot wear a ring. */}
              <span
                className="nxt tabular text-xl leading-none text-pretty sm:text-2xl"
                style={
                  tile.tone !== undefined &&
                  !kitEdge(sideOf(tile.tone === "a" ? "A" : "B").color, theme)
                    ? { color: sideOf(tile.tone === "a" ? "A" : "B").color }
                    : undefined
                }
              >
                {tile.value}
              </span>
            </Card>
          );
        })}
      </div>

      {durations.some((d) => d !== null) && (
        <Card className="gap-0 overflow-hidden p-0">
          <div
            className="relative flex items-center gap-2 border-b px-4 py-3"
            {...hintProps("pointLengths")}
          >
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("board.story.pointLengths")}
            </span>
            <Hint
              open={hint === "pointLengths"}
              onToggle={() => toggleHint("pointLengths")}
              text={t("board.story.hint.pointLengths")}
              align="left"
            />
          </div>
          <div className="px-4 py-4">
            <Lengths points={points} durations={durations} a={a} b={b} />
          </div>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {(["A", "B"] as const).map((s) => {
          const lines = scorersOf(points, s);
          const pairs = hookupsOf(points, s);
          return (
            <Card key={s} className="gap-0 overflow-hidden p-0">
              <div className="flex items-center gap-2 border-b px-4 py-3">
                <ColorDot color={sideOf(s).color} />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("board.story.scorers")} · {sideOf(s).short}
                </span>
              </div>
              {lines.length === 0 ? (
                <p className="px-4 py-3 text-sm text-muted-foreground">{t("board.noPoints")}</p>
              ) : (
                <div>
                  {lines.slice(0, 6).map((line) => (
                    // A scorer with a public profile is a link, the team page
                    // roster's convention: the whole row, the name underlined.
                    <RosterProfileRow
                      key={line.playerId}
                      profileId={profiles?.get(line.playerId)}
                      className="grid grid-cols-[minmax(0,1fr)_2.5rem_2.5rem] items-center gap-x-2 border-b px-4 py-2 text-sm last:border-b-0"
                    >
                      <span
                        className={cn(
                          "min-w-0 truncate font-medium",
                          profiles?.has(line.playerId) === true && "underline underline-offset-4",
                        )}
                      >
                        {nameOf(s, line.playerId)}
                      </span>
                      <span className="tabular text-right font-semibold">
                        {line.goals > 0 ? line.goals : "—"}
                      </span>
                      <span className="tabular text-right text-muted-foreground">
                        {line.assists > 0 ? line.assists : "—"}
                      </span>
                    </RosterProfileRow>
                  ))}
                  {pairs.length > 0 && (
                    <div className="border-t px-4 py-2.5">
                      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t("board.story.hookups")}
                      </span>
                      {pairs.slice(0, 3).map((h) => (
                        <div
                          key={`${h.scorerId}|${h.assistId}`}
                          className="flex items-baseline justify-between gap-2 text-sm"
                        >
                          <span className="min-w-0 truncate">
                            {linkedName(s, h.scorerId)}
                            <span className="text-muted-foreground">
                              {" "}
                              ← {linkedName(s, h.assistId)}
                            </span>
                          </span>
                          <span className="tabular shrink-0 font-semibold">×{h.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        <div
          className="relative flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b px-4 py-3"
          {...hintProps("score")}
        >
          <span className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("board.story.score")}
            </span>
            <Hint
              open={hint === "score"}
              onToggle={() => toggleHint("score")}
              text={t("board.story.hint.score")}
              align="left"
            />
          </span>
          <Legend a={a} b={b} />
        </div>
        <div className="px-4 pt-4 pb-2">
          <ScoreProgression
            snap={snap}
            target={target}
            a={a}
            b={b}
            half={half?.afterPoint ?? null}
            w={w}
          />
        </div>
        <div
          className="relative flex items-center gap-2 border-t px-4 py-3"
          {...hintProps("winProb")}
        >
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("board.story.winProb")}
          </span>
          <Hint
            open={hint === "winProb"}
            onToggle={() => toggleHint("winProb")}
            text={t("board.story.winProbHint", { n: target })}
            align="left"
          />
        </div>
        <div className="px-4 pt-1 pb-3">
          <WinProb series={series} a={a} b={b} w={w} />
        </div>
        <div className="border-t px-4 py-3">
          <div className="relative mb-2 flex items-center gap-2" {...hintProps("runOfPlay")}>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("board.runOfPlay")}
            </span>
            <Hint
              open={hint === "runOfPlay"}
              onToggle={() => toggleHint("runOfPlay")}
              text={t("board.story.hint.runOfPlay")}
              align="left"
            />
          </div>
          <RunStrip snap={snap} a={a} b={b} />
        </div>
      </Card>
    </div>
  );
}

/**
 * The explanation behind a number, behind a small question mark. The mark
 * is the point: nobody hovers a tile on the off-chance, and a mark says
 * there is something to read. The tile or header it sits in shows the text
 * on hover and toggles it on a tap (`hintProps`); the mark itself takes the
 * keyboard. Hand-rolled: a popover primitive would cost the main chunk more
 * than this page's data, and this is a paragraph in a box.
 */
function Hint({
  open,
  onToggle,
  text,
  align,
}: {
  open: boolean;
  onToggle: () => void;
  text: string;
  /** Which edge of the positioned parent the box hangs from. */
  align: "left" | "right";
}) {
  const t = useT();
  return (
    <>
      <button
        type="button"
        aria-label={t("board.story.whatIsThis")}
        aria-expanded={open}
        className="-m-1 shrink-0 rounded-full p-1 text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        // Keyboard only (`detail` is 0 for Enter and Space): a mouse's hover
        // has already opened it, and a finger's tap is the parent's.
        onClick={(e) => {
          if (e.detail === 0) onToggle();
        }}
      >
        <CircleHelpIcon className="size-3.5" />
      </button>
      {open && (
        <div
          role="note"
          className={cn(
            "pointer-events-none absolute top-full z-20 mt-1.5 w-[min(22rem,calc(100vw-3rem))] rounded-lg border bg-popover px-3 py-2 text-left text-xs font-normal normal-case tracking-normal text-popover-foreground shadow-lg",
            align === "left" ? "left-0" : "right-0",
          )}
        >
          {text}
        </div>
      )}
    </>
  );
}

function Legend({ a, b }: { a: StorySide; b: StorySide }) {
  // A kit the pane swallows (`kitEdge`) gets the hairline ring every painted
  // mark gets; on a 2px line that is most of the line, which is the point.
  const theme = useTheme();
  return (
    <span className="flex items-center gap-3 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <span
          aria-hidden="true"
          data-edge={kitEdge(a.color, theme)}
          className="kit-edge h-0.5 w-4 rounded-full"
          style={{ background: a.color }}
        />
        {a.short}
      </span>
      <span className="flex items-center gap-1.5">
        <span
          aria-hidden="true"
          data-edge={kitEdge(b.color, theme)}
          className="kit-edge h-0.5 w-4 rounded-full"
          style={{
            background: `repeating-linear-gradient(to right, ${b.color} 0 5px, transparent 5px 8px)`,
          }}
        />
        {b.short}
      </span>
    </span>
  );
}

/**
 * The charts' width in viewBox units. On a laptop 600; on a phone about the
 * phone's own pixels, so a 10-unit label paints at 10px instead of 6: the
 * scale is what makes a scaled SVG unreadable, not the chart.
 */
const W_WIDE = 600;
const W_NARROW = 340;
/** Tailwind's `sm`, the breakpoint the story's grid already uses. */
const SM_QUERY = "(min-width: 40rem)";
const H = 176;
const PAD = { l: 22, r: 16, t: 20, b: 22 };
const PH = 96;
const PPAD = { l: 38, r: 16, t: 12, b: 12 };

function ticksTo(target: number): number[] {
  if (target < 8) return [...new Set([0, Math.ceil(target / 2), target])];
  const out: number[] = [];
  for (let v = 0; v < target; v += 5) out.push(v);
  out.push(target);
  return out;
}

/** Points are equally spaced scoring events, not elapsed time. The connecting
 * lines guide the eye; the circles and the inspector carry the exact scores. */
function ScoreProgression({
  snap,
  target,
  a,
  b,
  half,
  w,
}: {
  snap: GameStats;
  target: number;
  a: StorySide;
  b: StorySide;
  half: number | null;
  w: number;
}) {
  const t = useT();
  const theme = useTheme();
  const [active, setActive] = useState<number | null>(null);
  const tooltipId = useId();
  const dots = worm(snap.points);
  const n = dots.length - 1;
  const selected = active ?? n;
  const shown = dots[selected] ?? { scoreA: 0, scoreB: 0 };
  const point = active === null ? undefined : snap.points[active - 1];
  const scoringSide = point?.team === "A" ? a : b;
  const roster = point?.team === "A" ? snap.teamA.roster : snap.teamB.roster;
  const scorer = roster?.find((p) => p.id === point?.scorerId);
  const assist = roster?.find((p) => p.id === point?.assistId);
  const xOf = (i: number): number => PAD.l + (i / Math.max(n, 1)) * (w - PAD.l - PAD.r);
  const yOf = (v: number): number => H - PAD.b - (v / target) * (H - PAD.t - PAD.b);
  const xTicks = [0, ...Array.from({ length: Math.floor((n - 1) / 5) }, (_, i) => (i + 1) * 5), n];
  const label =
    selected === n
      ? t("period.short.final")
      : selected === 0
        ? t("board.story.start")
        : t("board.story.pointN", { n: selected });
  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-4 px-1">
        <div className="flex items-center gap-3 sm:gap-5">
          {([a, b] as const).map((side, i) => (
            <div key={i === 0 ? "A" : "B"} className="flex items-center gap-2.5">
              <ColorDot color={side.color} />
              <span className="text-xs font-semibold text-muted-foreground">{side.short}</span>
              <span className="nxt tabular text-3xl leading-none sm:text-4xl">
                {i === 0 ? shown.scoreA : shown.scoreB}
              </span>
            </div>
          ))}
        </div>
        <span className="text-right text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          {label}
        </span>
      </div>
      <div className="relative">
        <div
          role="slider"
          tabIndex={0}
          aria-label={t("board.story.score")}
          aria-describedby={point ? tooltipId : undefined}
          aria-valuemin={0}
          aria-valuemax={n}
          aria-valuenow={selected}
          aria-valuetext={`${label}: ${a.short} ${shown.scoreA}, ${b.short} ${shown.scoreB}`}
          className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onPointerMove={(e) => {
            if (e.pointerType !== "mouse" && e.buttons === 0) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * w;
            setActive(
              Math.max(0, Math.min(n, Math.round(((x - PAD.l) / (w - PAD.l - PAD.r)) * n))),
            );
          }}
          onPointerDown={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * w;
            setActive(
              Math.max(0, Math.min(n, Math.round(((x - PAD.l) / (w - PAD.l - PAD.r)) * n))),
            );
          }}
          onPointerLeave={(e) => {
            if (e.pointerType === "mouse") setActive(null);
          }}
          onBlur={() => setActive(null)}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
              e.preventDefault();
              setActive(Math.max(0, selected - 1));
            } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
              e.preventDefault();
              setActive(Math.min(n, selected + 1));
            } else if (e.key === "Home") {
              e.preventDefault();
              setActive(0);
            } else if (e.key === "End") {
              e.preventDefault();
              setActive(n);
            } else if (e.key === "Escape") setActive(null);
          }}
        >
          <svg
            viewBox={`0 0 ${w} ${H}`}
            className="h-auto w-full cursor-crosshair"
            aria-hidden="true"
          >
            {ticksTo(target).map((v) => (
              <g key={v}>
                <line
                  x1={PAD.l}
                  x2={w - PAD.r}
                  y1={yOf(v)}
                  y2={yOf(v)}
                  stroke="currentColor"
                  strokeOpacity={v === 0 ? "0.16" : "0.07"}
                />
                <text
                  x={PAD.l - 8}
                  y={yOf(v) + 3}
                  textAnchor="end"
                  fontSize="9"
                  fill="currentColor"
                  fillOpacity="0.5"
                >
                  {v}
                </text>
              </g>
            ))}
            {dots.slice(1).map((dot, index) => {
              const prev = dots[index];
              if (!prev) return null;
              const lead = dot.scoreA - dot.scoreB || prev.scoreA - prev.scoreB;
              return (
                <path
                  key={`${dot.scoreA}-${dot.scoreB}`}
                  d={`M ${xOf(index)} ${yOf(prev.scoreA)} L ${xOf(index + 1)} ${yOf(dot.scoreA)} L ${xOf(index + 1)} ${yOf(dot.scoreB)} L ${xOf(index)} ${yOf(prev.scoreB)} Z`}
                  fill={lead > 0 ? a.color : b.color}
                  fillOpacity="0.08"
                />
              );
            })}
            {half !== null && half < n && (
              <g>
                <line
                  x1={xOf(half)}
                  x2={xOf(half)}
                  y1={PAD.t}
                  y2={H - PAD.b}
                  stroke="currentColor"
                  strokeOpacity="0.2"
                  strokeDasharray="3 4"
                />
                <text
                  x={xOf(half)}
                  y={9}
                  textAnchor="middle"
                  fontSize="8"
                  fontWeight="600"
                  letterSpacing="0.5"
                  fill="currentColor"
                  fillOpacity="0.5"
                >
                  {t("board.story.halfMark").toUpperCase()}
                </text>
              </g>
            )}
            {([a, b] as const).map((side, sideIndex) => {
              const key = sideIndex === 0 ? "scoreA" : "scoreB";
              const edge = kitEdge(side.color, theme);
              const d = dots
                .map((dot, i) => `${i === 0 ? "M" : "L"} ${xOf(i)} ${yOf(dot[key])}`)
                .join(" ");
              return (
                <g key={key}>
                  {edge && (
                    <path
                      d={d}
                      fill="none"
                      stroke="currentColor"
                      strokeOpacity="0.4"
                      strokeWidth="3.5"
                      strokeLinejoin="round"
                      strokeDasharray={sideIndex === 1 ? "5 3" : undefined}
                    />
                  )}
                  <path
                    d={d}
                    fill="none"
                    stroke={side.color}
                    strokeWidth="2"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    strokeDasharray={sideIndex === 1 ? "5 3" : undefined}
                  />
                  {dots.map((dot, i) => {
                    if (i === 0 || dot[key] === dots[i - 1]?.[key]) return null;
                    return (
                      <circle
                        key={`${dot.scoreA}-${dot.scoreB}`}
                        cx={xOf(i)}
                        cy={yOf(dot[key])}
                        data-point-team={sideIndex === 0 ? "A" : "B"}
                        data-active-point={i === active ? "true" : undefined}
                        r={i === selected ? 4 : 2.5}
                        fill={i === active ? side.color : "var(--card)"}
                        stroke={edge ? "currentColor" : side.color}
                        strokeWidth="1.5"
                      />
                    );
                  })}
                </g>
              );
            })}
            {active !== null && (
              <g>
                <line
                  x1={xOf(active)}
                  x2={xOf(active)}
                  y1={PAD.t}
                  y2={H - PAD.b}
                  stroke="currentColor"
                  strokeOpacity="0.3"
                  strokeDasharray="2 3"
                />
              </g>
            )}
            {xTicks.map((v) => (
              <text
                key={v}
                x={xOf(v)}
                y={H - 5}
                textAnchor="middle"
                fontSize="9"
                fill="currentColor"
                fillOpacity="0.5"
              >
                {v}
              </text>
            ))}
          </svg>
        </div>
        {point && active !== null && (
          <div
            id={tooltipId}
            role="tooltip"
            className="pointer-events-none absolute z-10 w-56 max-w-full rounded-lg border bg-popover p-3 text-xs text-popover-foreground shadow-lg"
            style={{
              left: `clamp(0px, calc(${(xOf(active) / w) * 100}% - 7rem), calc(100% - 14rem))`,
              // Put the tooltip opposite the scoring dot, with enough room for names on phones.
              ...(yOf(point.team === "A" ? point.scoreA : point.scoreB) > H / 2
                ? { top: 0 }
                : { bottom: "1.5rem" }),
            }}
          >
            <div className="mb-2 flex items-center gap-1.5 border-b pb-2 font-semibold">
              <ColorDot color={scoringSide.color} />
              <span>
                {t("board.story.pointN", { n: active })} · {scoringSide.short}
              </span>
              <span className="tabular ml-auto">
                {point.scoreA}–{point.scoreB}
              </span>
            </div>
            {scorer === undefined && assist === undefined ? (
              <p className="text-muted-foreground">{t("board.story.noCredits")}</p>
            ) : (
              <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5">
                <dt className="text-muted-foreground">{t("board.story.scorer")}</dt>
                <dd className="min-w-0 break-words font-medium">
                  {scorer ? playerLabel(scorer) : t("board.story.notRecorded")}
                </dd>
                <dt className="text-muted-foreground">{t("board.story.assist")}</dt>
                <dd className="min-w-0 break-words font-medium">
                  {assist ? playerLabel(assist) : t("board.story.notRecorded")}
                </dd>
              </dl>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Team A's chance after every point as a BAND: the area above the half line
 * in A's colour, below it in B's, so the picture says who was favoured and
 * when, in under half the worm's height.
 */
function WinProb({ series, a, b, w }: { series: number[]; a: StorySide; b: StorySide; w: number }) {
  const theme = useTheme();
  const id = useId();
  const n = series.length - 1;
  const xOf = (i: number): number => PPAD.l + (i / Math.max(n, 1)) * (w - PPAD.l - PPAD.r);
  const yOf = (p: number): number => PPAD.t + (1 - p) * (PH - PPAD.t - PPAD.b);
  const mid = yOf(0.5);
  const line = series.map((p, i) => `${i === 0 ? "M" : "L"} ${xOf(i)} ${yOf(p)}`).join(" ");
  const areaA = `${line} L ${xOf(n)} ${mid} L ${xOf(0)} ${mid} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${PH}`} className="h-auto w-full" role="img" aria-hidden="true">
      <defs>
        <clipPath id={`${id}-above`}>
          <rect x="0" y="0" width={w} height={mid} />
        </clipPath>
        <clipPath id={`${id}-below`}>
          <rect x="0" y={mid} width={w} height={PH - mid} />
        </clipPath>
      </defs>
      <path d={areaA} fill={a.color} fillOpacity="0.14" clipPath={`url(#${id}-above)`} />
      <path d={areaA} fill={b.color} fillOpacity="0.14" clipPath={`url(#${id}-below)`} />
      <line
        x1={PPAD.l}
        x2={w - PPAD.r}
        y1={mid}
        y2={mid}
        stroke="currentColor"
        strokeOpacity="0.35"
      />
      <text
        x={PPAD.l - 6}
        y={yOf(1) + 8}
        textAnchor="end"
        fontSize="9"
        fontWeight="700"
        fill={kitEdge(a.color, theme) ? "currentColor" : a.color}
      >
        {a.short}
      </text>
      <text
        x={PPAD.l - 6}
        y={yOf(0) - 2}
        textAnchor="end"
        fontSize="9"
        fontWeight="700"
        fill={kitEdge(b.color, theme) ? "currentColor" : b.color}
      >
        {b.short}
      </text>
      {([a, b] as const).map((side, i) => (
        <path
          key={i === 0 ? "A" : "B"}
          d={line}
          fill="none"
          stroke={kitEdge(side.color, theme) ? "currentColor" : side.color}
          strokeWidth="1.75"
          strokeLinejoin="round"
          strokeLinecap="round"
          clipPath={`url(#${id}-${i === 0 ? "above" : "below"})`}
        />
      ))}
      <circle
        cx={xOf(n)}
        cy={yOf(series[n] ?? 0.5)}
        r="3"
        fill="var(--card)"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

/** The live card's blocks: one per point, the scorer's colour, a break marked
 *  with a brand underline. */
function RunStrip({ snap, a, b }: { snap: GameStats; a: StorySide; b: StorySide }) {
  const theme = useTheme();
  return (
    <div className="flex h-7 gap-px">
      {snap.points.map((p) => (
        <span
          key={`${p.at}-${p.scoreA}-${p.scoreB}`}
          title={`${p.scoreA}–${p.scoreB}`}
          data-edge={kitEdge(p.team === "A" ? a.color : b.color, theme)}
          className={cn(
            "kit-edge min-w-0 flex-1 rounded-[2px]",
            isBreak(p) && "border-b-[3px] border-brand",
          )}
          style={{ background: p.team === "A" ? a.color : b.color, opacity: isBreak(p) ? 1 : 0.7 }}
        />
      ))}
    </div>
  );
}

/** A bar per point by how long it took, in the scorer's colour. */
/**
 * One bar per point, its height the point's length, its colour the scorer's.
 * Hover, tap or focus a bar and a label names it: the length, the point, who
 * scored and the score after it. A tap elsewhere puts it away; a mouse
 * leaving the bar does too. The first point has no length (nothing before
 * it to measure from), so its label carries no time.
 */
function Lengths({
  points,
  durations,
  a,
  b,
}: {
  points: GameStats["points"];
  durations: (number | null)[];
  a: StorySide;
  b: StorySide;
}) {
  const max = Math.max(...durations.map((d) => d ?? 0), 1);
  const t = useT();
  const theme = useTheme();
  const [active, setActive] = useState<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (active === null) return;
    const away = (e: PointerEvent): void => {
      if (box.current !== null && e.target instanceof Node && box.current.contains(e.target))
        return;
      setActive(null);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [active]);
  const n = points.length;
  const shown = active === null ? undefined : points[active];
  const shownMs = active === null ? null : (durations[active] ?? null);
  return (
    <div ref={box} className="relative">
      {active !== null && shown !== undefined && (
        <div
          className="pointer-events-none absolute bottom-full z-10 mb-1.5 whitespace-nowrap rounded-md border bg-popover px-2 py-1 text-[11px] leading-4 text-popover-foreground shadow-md"
          style={{
            left: `${((active + 0.5) / n) * 100}%`,
            // Centred on its bar, except near either edge, where centring
            // would push it out of the card.
            transform:
              active < n * 0.2
                ? "translateX(-8%)"
                : active > n * 0.8
                  ? "translateX(-92%)"
                  : "translateX(-50%)",
          }}
        >
          {shownMs !== null && <span className="tabular font-semibold">{mmss(shownMs)} · </span>}
          {t("board.story.pointN", { n: active + 1 })} ·{" "}
          {/* The team's code in its kit colour, unless the pane would swallow
              it (a white kit on the light card): then plain ink. A word cannot
              wear a ring. */}
          <span
            style={{
              color: kitEdge(shown.team === "A" ? a.color : b.color, theme)
                ? undefined
                : shown.team === "A"
                  ? a.color
                  : b.color,
            }}
          >
            {shown.team === "A" ? a.short : b.short}
          </span>{" "}
          <span className="tabular">
            {shown.scoreA}–{shown.scoreB}
          </span>
        </div>
      )}
      <div className="flex h-24 items-end gap-px">
        {points.map((p, i) => {
          const ms = durations[i] ?? null;
          const height = ms === null ? 4 : Math.max(4, (ms / max) * 96);
          const short = p.team === "A" ? a.short : b.short;
          return (
            <button
              type="button"
              key={`${p.at}-${p.scoreA}-${p.scoreB}`}
              aria-label={`${t("board.story.pointN", { n: i + 1 })} · ${short} ${p.scoreA}–${p.scoreB}${ms === null ? "" : ` · ${mmss(ms)}`}`}
              aria-pressed={active === i}
              data-edge={kitEdge(p.team === "A" ? a.color : b.color, theme)}
              className="kit-edge min-w-0 flex-1 rounded-t-[2px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{
                height,
                background: p.team === "A" ? a.color : b.color,
                opacity: active === i ? 1 : ms === null ? 0.25 : 0.8,
              }}
              onPointerEnter={(e) => {
                if (e.pointerType === "mouse") setActive(i);
              }}
              onPointerLeave={(e) => {
                if (e.pointerType === "mouse") setActive((cur) => (cur === i ? null : cur));
              }}
              // A finger: tap shows, tap again hides. Not `onClick`, which a
              // mouse also fires and would hide what its hover just showed.
              onPointerDown={(e) => {
                if (e.pointerType !== "mouse") setActive((cur) => (cur === i ? null : i));
              }}
              onFocus={() => setActive(i)}
              onBlur={() => setActive((cur) => (cur === i ? null : cur))}
            />
          );
        })}
      </div>
    </div>
  );
}

function mmss(ms: number): string {
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function minutes(ms: number): string {
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${(m % 60).toString().padStart(2, "0")} min`;
}
